"use client";

import {
  Bell,
  Cable,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Gauge,
  Layers3,
  MapPinned,
  Plus,
  QrCode,
  Undo2,
  Wind,
  Wrench,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { Card, ThemedMultiSelect } from "@/components/ui-system";
import { Button } from "@/components/ds/button";
import { listUsers } from "@/lib/api/endpoints/users";
import type { UserDto } from "@/lib/api/types/enterprise";
import { cn } from "@/lib/utils/cn";
import { useAuthStore } from "@/modules/auth/store";

import {
  SAMPLING_RATE_PERIOD_OPTIONS,
  type DataloggerAlertAudienceKey,
  type DataloggerFormSnapshot,
  type SamplingRatePeriod,
  type ChannelRow,
  QR_PUBLIC_FIELD_OPTIONS,
  validateAlertTemplates,
  validateBasic,
  validateChannels,
  validateConnectivityAndProtocol,
  validateHardware,
  validateVisualizations,
} from "../schema/datalogger.schema";
import { useDataloggerWizardStore, WIZARD_STEP_LABELS } from "../hooks/use-datalogger-form";
import { useWarrantyPreview } from "../hooks/use-warranty-preview";
import {
  wizardFieldClass,
  wizardInvalidInputClass,
  wizardKickerClass,
  wizardLabelClass,
  wizardSectionShellClass,
  wizardSectionShellDenseClass,
  wizardToggleCardClass,
} from "./form-primitives";
import { StepTabs } from "./step-tabs";
import { StepBasic } from "./steps/step-basic";
import { StepBasicConnectivity } from "./steps/step-basic-connectivity";
import { StepChannels } from "./steps/step-channels";
import { StepReview } from "./steps/step-review";

export const INVALID_FIELD_ATTR = "data-validation-key";
const TAB_ICONS = [MapPinned, Layers3, Wind, Gauge, Bell, Cable, Wrench, QrCode, CheckCircle2] as const;
function validateCreateTab(tab: number, snapshot: DataloggerFormSnapshot): string[] {
  if (tab === 0) return validateBasic(snapshot.basic);
  if (tab === 1) return validateChannels(snapshot.channels);
  if (tab === 2) return validateVisualizations(snapshot.visualizations, snapshot.channels);
  if (tab === 3) {
    const err: string[] = [];
    if (!Number.isFinite(snapshot.hardware.samplingRateValue) || snapshot.hardware.samplingRateValue <= 0) {
      err.push("Sampling rate value must be greater than zero.");
    }
    if (!Number.isFinite(snapshot.hardware.samplingRate) || snapshot.hardware.samplingRate <= 0) {
      err.push("Effective sampling rate must be greater than zero.");
    }
    return err;
  }
  if (tab === 4) return validateAlertTemplates(snapshot.alerts, snapshot.channels);
  if (tab === 5) return validateConnectivityAndProtocol(snapshot.hardware);
  if (tab === 6) return [];
  if (tab === 7 && snapshot.qrPublic.enabled && snapshot.qrPublic.displayFields.length === 0) {
    return ["Select at least one public QR field or disable the public QR page."];
  }
  if (tab === 8) {
    return [
        ...validateBasic(snapshot.basic),
        ...validateHardware(snapshot.hardware),
        ...validateChannels(snapshot.channels),
        ...validateAlertTemplates(snapshot.alerts, snapshot.channels),
        ...validateVisualizations(snapshot.visualizations, snapshot.channels),
      ];
  }
  return [];
}

export function QrPublicTab({ validationError }: { validationError?: string | null }) {
  const qrPublic = useDataloggerWizardStore((s) => s.qrPublic);
  const setQrPublic = useDataloggerWizardStore((s) => s.setQrPublic);
  return (
    <Card className="mx-auto max-w-3xl border-card-border p-ds-6 shadow-card">
      <div className="flex items-start gap-ds-3">
        <span className="rounded-control bg-primary/10 p-ds-2 text-primary"><QrCode className="h-5 w-5" /></span>
        <div>
          <h2 className="text-title font-semibold text-foreground">QR public page</h2>
          <p className="mt-1 text-caption text-muted">A QR code opens a safe public device summary. Internal information, including category, is never available.</p>
        </div>
      </div>
      <label className="mt-ds-6 flex cursor-pointer items-center justify-between gap-ds-4 rounded-control border border-card-border bg-surface-primary px-ds-4 py-ds-3">
        <span><span className="block font-semibold text-foreground">Enable public QR page</span><span className="text-caption text-muted">Enabled by default. Disable it to make the QR link unavailable.</span></span>
        <input type="checkbox" className="h-4 w-4 accent-primary" checked={qrPublic.enabled} onChange={(event) => setQrPublic({ enabled: event.target.checked })} />
      </label>
      <fieldset disabled={!qrPublic.enabled} className="mt-ds-5">
        <legend className="text-caption font-semibold text-foreground">Information shown after scanning</legend>
        <div className="mt-ds-3 grid gap-ds-2 sm:grid-cols-2">
          {QR_PUBLIC_FIELD_OPTIONS.map((option) => {
            const checked = qrPublic.displayFields.includes(option.value);
            return <label key={option.value} className="flex cursor-pointer items-center gap-ds-3 rounded-control border border-card-border px-ds-3 py-ds-2 text-caption text-foreground">
              <input type="checkbox" className="h-4 w-4 accent-primary" checked={checked} onChange={() => setQrPublic({ displayFields: checked ? qrPublic.displayFields.filter((field) => field !== option.value) : [...qrPublic.displayFields, option.value] })} />
              {option.label}
            </label>;
          })}
        </div>
      </fieldset>
      {validationError ? <p className="mt-ds-3 text-caption text-error" role="alert">{validationError}</p> : null}
    </Card>
  );
}

function panelInputClass() {
  return cn(wizardFieldClass, "px-ds-3");
}

export function invalidFieldKeyForError(error: string): string | null {
  if (error === "Name is required.") return "basic.name";
  if (error.includes("Latitude") || error.includes("longitude")) return "basic.coordinates";
  if (error.includes("Sampling rate value") || error.includes("Effective sampling rate")) return "hardware.samplingRateValue";
  if (error.startsWith("Polling:")) return "protocol.pollIntervalSeconds";
  if (error.startsWith("MQTT:")) return "protocol.mqttPort";
  if (error.startsWith("WiFi: local backend host") || error.startsWith("Ethernet: local backend host")) return "protocol.lanServerHost";
  if (error.startsWith("WiFi: local backend port") || error.startsWith("Ethernet: local backend port")) return "protocol.lanServerPort";
  if (error.startsWith("WiFi: SSID")) return "protocol.wifiSsid";
  if (error.startsWith("WiFi: password")) return "protocol.wifiPassword";
  if (error.startsWith("WiFi: static IP")) return "protocol.wifiStaticIp";
  if (error.startsWith("WiFi: gateway")) return "protocol.wifiGateway";
  if (error.startsWith("WiFi: netmask")) return "protocol.wifiNetmask";
  if (error.startsWith("Bluetooth: Windows COM port")) return "protocol.bluetoothPort";
  if (error.startsWith("Bluetooth: baud rate")) return "protocol.bluetoothBaudRate";
  if (error.startsWith("Ethernet: static IP")) return "protocol.ethernetIp";
  if (error.startsWith("Ethernet: gateway")) return "protocol.ethernetGateway";
  if (error.startsWith("Ethernet: netmask")) return "protocol.ethernetNetmask";
  if (error.startsWith("Modbus TCP: host")) return "protocol.modbusHost";
  if (error.startsWith("Modbus TCP: port")) return "protocol.modbusPort";
  if (error.startsWith("LoRaWAN: AppEUI")) return "protocol.loraAppEui";
  if (error.startsWith("LoRaWAN: DevEUI")) return "protocol.loraDevEui";
  if (error.startsWith("LoRaWAN: AppKey")) return "protocol.loraAppKey";
  if (error === "Wind rose: wind speed channel is required.") return "visualizations.windRose.speedChannelKey";
  if (error === "Wind rose: wind direction channel is required.") return "visualizations.windRose.directionChannelKey";
  if (error.includes("selected wind speed channel")) return "visualizations.windRose.speedChannelKey";
  if (error.includes("selected wind direction channel")) return "visualizations.windRose.directionChannelKey";
  if (error.includes("speed and direction channels must be different")) return "visualizations.windRose.directionChannelKey";

  const alertMatch = error.match(/^Alert (\d+):\s*(.*)$/);
  if (alertMatch) {
    const index = Number(alertMatch[1]) - 1;
    const message = alertMatch[2] ?? "";
    if (message === "Alert channel is required." || message === "Alert channel must match a configured channel.") {
      return `alerts.${index}.channelKey`;
    }
    if (message === "Alert threshold must be a valid number.") return `alerts.${index}.threshold`;
  }

  const channelMatch = error.match(/^Channel (\d+):\s*(.*)$/);
  if (channelMatch) {
    const index = Number(channelMatch[1]) - 1;
    const message = channelMatch[2] ?? "";
    if (message.includes("name is required")) return `channel.${index}.name`;
    if (message.includes("key")) return `channel.${index}.key`;
    if (message.includes("minimum") || message.includes("warning")) return `channel.${index}.thresholds`;
    if (message.includes("formula")) return `channel.${index}.formula`;
  }

  return null;
}

function invalidFieldClass(invalid: boolean) {
  return invalid ? wizardInvalidInputClass : "";
}

function toggleCardClass(checked: boolean) {
  return wizardToggleCardClass(checked);
}

const NOTIFICATION_AUDIENCE_META = [
  { key: "admin", label: "Admin", hint: "Workspace admins" },
  { key: "distributor", label: "Distributor", hint: "Distributor contacts" },
  { key: "users", label: "Users", hint: "Assigned users" },
] as const;

const NOTIFICATION_CHANNEL_META = [
  { key: "email", label: "Email" },
  { key: "message", label: "Message" },
  { key: "inApp", label: "In app" },
] as const;

function samplingPeriodLabel(period: SamplingRatePeriod): string {
  return SAMPLING_RATE_PERIOD_OPTIONS.find((o) => o.value === period)?.label ?? "Per second";
}

export function DataSamplingTab({
  invalidFieldKey,
  onFieldEdit,
}: {
  invalidFieldKey: string | null;
  onFieldEdit: () => void;
}) {
  const draft = useDataloggerWizardStore((s) => s.hardwareDraft);
  const setHardwareDraft = useDataloggerWizardStore((s) => s.setHardwareDraft);
  const samplingInvalid = invalidFieldKey === "hardware.samplingRateValue";
  const samplingError = samplingInvalid ? "Sampling rate value must be greater than zero." : null;
  const sampleCountLabel = `${draft.samplingRateValue} sample${draft.samplingRateValue === 1 ? "" : "s"}`;
  const selectedPeriodLabel = samplingPeriodLabel(draft.samplingRatePeriod);

  return (
    <div className="grid gap-ds-4 lg:grid-cols-[minmax(0,1fr)_18rem]">
      <Card className={cn(wizardSectionShellClass, "space-y-ds-4")}>
        <div>
          <p className={wizardKickerClass}>Sampling policy</p>
          <h3 className="mt-ds-1 text-title font-semibold text-foreground">Cadence</h3>
          <p className="mt-ds-1 text-caption text-muted">
            Compact defaults for ingest cadence. These values are stored in metadata and can be tuned after provisioning.
          </p>
        </div>
        <div className="grid gap-ds-3 sm:grid-cols-2">
          <label className="space-y-ds-1">
            <span className={wizardLabelClass}>Samples</span>
            <input
              className={cn(panelInputClass(), "tabular-nums", invalidFieldClass(samplingInvalid))}
              data-validation-key="hardware.samplingRateValue"
              aria-invalid={samplingInvalid || undefined}
              aria-describedby={samplingInvalid ? "hardware-sampling-rate-error" : undefined}
              type="number"
              min={1}
              value={draft.samplingRateValue}
              onChange={(e) => {
                onFieldEdit();
                setHardwareDraft({ samplingRateValue: Number(e.target.value) || 1 });
              }}
            />
            {samplingError ? (
              <span id="hardware-sampling-rate-error" className="text-caption text-error">
                {samplingError}
              </span>
            ) : null}
          </label>
          <label className="space-y-ds-1">
            <span className={wizardLabelClass}>Period</span>
            <select
              className={panelInputClass()}
              value={draft.samplingRatePeriod}
              onChange={(e) => {
                onFieldEdit();
                setHardwareDraft({
                  samplingRatePeriod: e.target.value as SamplingRatePeriod,
                });
              }}
            >
              {SAMPLING_RATE_PERIOD_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </Card>

      <Card className={wizardSectionShellClass}>
        <p className={wizardKickerClass}>Effective rate</p>
        <p className="mt-ds-3 text-[2.25rem] font-bold tabular-nums leading-none text-foreground">
          {sampleCountLabel}
        </p>
        <p className="mt-ds-1 text-title font-medium text-primary">{selectedPeriodLabel}</p>
        <p className="mt-ds-3 text-caption text-muted">
          Used by fleet health and delay calculations once telemetry starts flowing.
        </p>
      </Card>
    </div>
  );
}

function visualizationChannelLabel(channel: ChannelRow): string {
  const key = channel.channelKey.trim();
  return key && key !== channel.name.trim() ? `${channel.name.trim()} (${key})` : channel.name.trim() || key;
}

export function VisualizationsTab({
  invalidFieldKey,
  onFieldEdit,
}: {
  invalidFieldKey: string | null;
  onFieldEdit: () => void;
}) {
  const channels = useDataloggerWizardStore((s) => s.channels);
  const windRose = useDataloggerWizardStore((s) => s.visualizations.windRose);
  const setWindRose = useDataloggerWizardStore((s) => s.setWindRose);

  const numericChannels = useMemo(
    () => channels.filter((channel) => channel.dataType === "float" || channel.dataType === "int" || channel.dataType === "formula"),
    [channels],
  );
  const speedInvalid = invalidFieldKey === "visualizations.windRose.speedChannelKey";
  const directionInvalid = invalidFieldKey === "visualizations.windRose.directionChannelKey";

  return (
    <div className="grid gap-ds-4 lg:grid-cols-[minmax(0,1fr)_18rem]">
      <Card className={cn(wizardSectionShellClass, "space-y-ds-4")}>
        <div>
          <p className={wizardKickerClass}>Device visualizations</p>
          <h3 className="mt-ds-1 text-title font-semibold text-foreground">Wind rose</h3>
          <p className="mt-ds-1 text-caption text-muted">
            Pair a wind speed channel with a wind direction channel to unlock a live wind rose for this logger.
          </p>
        </div>

        <button
          type="button"
          className={cn(
            toggleCardClass(windRose.enabled),
            windRose.enabled && "border-success/30 bg-success/10",
          )}
          onClick={() => {
            onFieldEdit();
            setWindRose({ enabled: !windRose.enabled });
          }}
        >
          <span
            className={cn(
              "mt-0.5 inline-flex h-6 w-11 items-center rounded-full border p-0.5 transition-colors",
              windRose.enabled
                ? "border-success/40 bg-success shadow-card"
                : "border-border/60 bg-muted-bg/60",
            )}
            aria-hidden
          >
            <span
              className={cn(
                "block h-[18px] w-[18px] rounded-full bg-card shadow-sm transition-transform",
                windRose.enabled ? "translate-x-5" : "translate-x-0",
              )}
            />
          </span>
          <span className="min-w-0 text-left">
            <span className="block text-caption font-semibold text-foreground">
              Enable wind rose on Live page
              <span className={cn("ml-ds-2 text-micro", windRose.enabled ? "text-success" : "text-muted")}>
                {windRose.enabled ? "On" : "Off"}
              </span>
            </span>
            <span className="mt-1 block text-micro text-muted">
              Best for wind loggers where direction is recorded in degrees and speed in a consistent unit.
            </span>
          </span>
        </button>

        {windRose.enabled ? (
          <div className="grid gap-ds-3 sm:grid-cols-2">
            <label className="space-y-ds-1">
              <span className={wizardLabelClass}>Wind speed channel</span>
              <select
                className={cn(panelInputClass(), invalidFieldClass(speedInvalid))}
                data-validation-key="visualizations.windRose.speedChannelKey"
                value={windRose.speedChannelKey}
                onChange={(e) => {
                  onFieldEdit();
                  setWindRose({ speedChannelKey: e.target.value });
                }}
              >
                <option value="">Select speed channel</option>
                {numericChannels.map((channel) => (
                  <option key={channel.id} value={channel.channelKey}>
                    {visualizationChannelLabel(channel)}
                  </option>
                ))}
              </select>
            </label>

            <label className="space-y-ds-1">
              <span className={wizardLabelClass}>Wind direction channel</span>
              <select
                className={cn(panelInputClass(), invalidFieldClass(directionInvalid))}
                data-validation-key="visualizations.windRose.directionChannelKey"
                value={windRose.directionChannelKey}
                onChange={(e) => {
                  onFieldEdit();
                  setWindRose({ directionChannelKey: e.target.value });
                }}
              >
                <option value="">Select direction channel</option>
                {numericChannels.map((channel) => (
                  <option key={channel.id} value={channel.channelKey}>
                    {visualizationChannelLabel(channel)}
                  </option>
                ))}
              </select>
            </label>
          </div>
        ) : null}
      </Card>

      <Card className={wizardSectionShellClass}>
        <p className={wizardKickerClass}>Live behavior</p>
        <dl className="mt-ds-4 space-y-ds-3 text-caption">
          <div>
            <dt className="text-muted">Status</dt>
            <dd className="mt-0.5 text-foreground">{windRose.enabled ? "Enabled" : "Disabled"}</dd>
          </div>
          <div>
            <dt className="text-muted">Speed channel</dt>
            <dd className="mt-0.5 text-foreground">{windRose.speedChannelKey || "Not selected"}</dd>
          </div>
          <div>
            <dt className="text-muted">Direction channel</dt>
            <dd className="mt-0.5 text-foreground">{windRose.directionChannelKey || "Not selected"}</dd>
          </div>
        </dl>
      </Card>
    </div>
  );
}

const ALERT_OPERATORS = [">", "<", ">=", "<=", "=", "==", "!="] as const;

function userMatchesAudienceRole(user: UserDto, roleKey: DataloggerAlertAudienceKey): boolean {
  const role = (user.role || "").trim().toLowerCase();
  if (roleKey === "admin") {
    return role === "admin";
  }
  if (roleKey === "distributor") {
    return role === "distributor";
  }
  return role === "user";
}

function userDisplayName(user: UserDto): string {
  return user.name?.trim() || user.email;
}

function alertSourceLabel(source: "manual" | "warning_low" | "warning_high"): string {
  if (source === "warning_low") return "Auto - warning low";
  if (source === "warning_high") return "Auto - warning high";
  return "Manual";
}

export function AlertsTab({
  invalidFieldKey,
  validationErrors,
  onFieldEdit,
}: {
  invalidFieldKey: string | null;
  validationErrors: string[];
  onFieldEdit: () => void;
}) {
  const channels = useDataloggerWizardStore((s) => s.channels);
  const alertConditions = useDataloggerWizardStore((s) => s.alertConditions);
  const alertRecipients = useDataloggerWizardStore((s) => s.alertRecipients);
  const setAlertCondition = useDataloggerWizardStore((s) => s.setAlertCondition);
  const addAlertCondition = useDataloggerWizardStore((s) => s.addAlertCondition);
  const removeAlertCondition = useDataloggerWizardStore((s) => s.removeAlertCondition);
  const setAlertRecipients = useDataloggerWizardStore((s) => s.setAlertRecipients);
  const tenantId = useAuthStore((s) => s.tenantId);
  const usersQuery = useQuery({
    queryKey: ["users", tenantId ?? "", "wizard-alert-audience"],
    queryFn: async () => (await listUsers(tenantId)).items,
    enabled: Boolean(tenantId),
    staleTime: 30_000,
  });
  const workspaceUsers = usersQuery.data ?? [];

  function updateAlert(index: number, patch: Parameters<typeof setAlertCondition>[1]) {
    onFieldEdit();
    setAlertCondition(index, patch);
  }

  return (
    <div className="space-y-ds-4">
      <Card className={cn(wizardSectionShellClass, "space-y-ds-5")}>
        <div>
          <p className={wizardKickerClass}>Reliability</p>
          <h3 className="mt-ds-1 text-title font-semibold text-foreground">Alert conditions</h3>
          <p className="mt-ds-1 text-caption text-muted">
            Warning low and warning high values from Channels are added here automatically. Each condition provisions one device-scoped alert rule after create.
          </p>
        </div>

        <div className="grid gap-ds-4 xl:grid-cols-[minmax(0,1.35fr)_minmax(22rem,0.9fr)]">
          <div className="space-y-ds-3">
            {alertConditions.length > 0 ? (
              alertConditions.map((alertCondition, index) => {
                const fieldError =
                  validationErrors.find((error) => invalidFieldKeyForError(error) === `alerts.${index}.channelKey`) ??
                  validationErrors.find((error) => invalidFieldKeyForError(error) === `alerts.${index}.threshold`) ??
                  null;
                const channelInvalid = invalidFieldKey === `alerts.${index}.channelKey`;
                const thresholdInvalid = invalidFieldKey === `alerts.${index}.threshold`;
                const selectedChannelKey = channels.some((channel) => channel.channelKey === alertCondition.channelKey)
                  ? alertCondition.channelKey
                  : channels[0]?.channelKey ?? "";
                const removeDisabled = alertCondition.source !== "manual";

                return (
                  <div key={`alert-condition-${index}`} className={wizardSectionShellDenseClass}>
                    <div className="mb-ds-3 flex flex-wrap items-start justify-between gap-ds-3">
                      <div>
                        <p className="text-micro font-bold uppercase tracking-[0.14em] text-muted">Condition {index + 1}</p>
                        <p className="mt-ds-1 text-caption text-muted">{alertSourceLabel(alertCondition.source)}</p>
                      </div>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        disabled={removeDisabled}
                        title={removeDisabled ? "Clear the threshold in Channels to remove this auto condition." : undefined}
                        onClick={() => {
                          onFieldEdit();
                          removeAlertCondition(index);
                        }}
                      >
                        Remove
                      </button>
                    </div>

                    <div className="grid gap-ds-2 sm:grid-cols-[minmax(0,1.3fr)_5rem_minmax(0,1fr)]">
                      <label className="space-y-1">
                        <span className="text-micro text-muted">Field path</span>
                        <select
                          className={cn(panelInputClass(), channelInvalid && invalidFieldClass(true))}
                          data-validation-key={`alerts.${index}.channelKey`}
                          aria-invalid={channelInvalid || undefined}
                          aria-describedby={channelInvalid ? `alerts-${index}-channel-error` : undefined}
                          value={selectedChannelKey}
                          onChange={(e) => {
                            const nextChannel = channels.find((channel) => channel.channelKey === e.target.value);
                            updateAlert(index, { channelKey: e.target.value, channelId: nextChannel?.id ?? null });
                          }}
                        >
                          {channels.map((channel) => (
                            <option key={channel.id} value={channel.channelKey}>
                              {channel.name}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="space-y-1">
                        <span className="text-micro text-muted">Op</span>
                        <select
                          className={panelInputClass()}
                          value={alertCondition.operator}
                          onChange={(e) => updateAlert(index, { operator: e.target.value as typeof alertCondition.operator })}
                        >
                          {ALERT_OPERATORS.map((op) => (
                            <option key={op} value={op}>
                              {op}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="space-y-1">
                        <span className="text-micro text-muted">Value</span>
                        <input
                          className={cn(panelInputClass(), thresholdInvalid && invalidFieldClass(true))}
                          data-validation-key={`alerts.${index}.threshold`}
                          aria-invalid={thresholdInvalid || undefined}
                          aria-describedby={thresholdInvalid ? `alerts-${index}-threshold-error` : undefined}
                          value={alertCondition.threshold}
                          onChange={(e) => updateAlert(index, { threshold: e.target.value })}
                          placeholder="80"
                        />
                      </label>
                    </div>
                    {channelInvalid && fieldError ? (
                      <p id={`alerts-${index}-channel-error`} className="text-caption text-error">
                        {fieldError.replace(/^Alert \d+:\s*/, "")}
                      </p>
                    ) : null}
                    {thresholdInvalid && fieldError ? (
                      <p id={`alerts-${index}-threshold-error`} className="text-caption text-error">
                        {fieldError.replace(/^Alert \d+:\s*/, "")}
                      </p>
                    ) : null}
                  </div>
                );
              })
            ) : (
              <div className="rounded-control border border-dashed border-card-border bg-surface-inset px-ds-4 py-ds-5 text-caption text-muted">
                No conditions yet. Set warning low / high values in Channels, or add a manual condition below.
              </div>
            )}

            <button
              type="button"
              className="inline-flex items-center gap-ds-1 text-caption text-link"
              onClick={() => {
                onFieldEdit();
                addAlertCondition();
              }}
            >
              <Plus className="h-3.5 w-3.5" aria-hidden />
              Add condition
            </button>
          </div>

          <div className={cn(wizardSectionShellDenseClass, "space-y-ds-4 xl:sticky xl:top-ds-4 xl:self-start")}>
            <div>
              <p className={wizardKickerClass}>Notifications</p>
              <h4 className="mt-ds-1 text-body font-semibold text-foreground">Recipients</h4>
              <p className="mt-ds-1 text-caption text-muted">These settings apply to every alert condition created from this device flow.</p>
            </div>
            <div className="space-y-ds-3">
              <div>
                <p className={wizardLabelClass}>Audience</p>
                <div className="mt-ds-2 grid gap-ds-2">
                  {NOTIFICATION_AUDIENCE_META.map(({ key, label, hint }) => {
                    const prefs = alertRecipients[key];
                    const checked = prefs.email || prefs.message || prefs.inApp;
                    const roleUsers = workspaceUsers
                      .filter((user) => userMatchesAudienceRole(user, key))
                      .sort((a, b) => userDisplayName(a).localeCompare(userDisplayName(b)));
                    return (
                      <div key={key} className={toggleCardClass(checked)}>
                        <span className="min-w-0 flex-1">
                          <span className="block text-caption font-medium text-foreground">{label}</span>
                          <span className="mt-0.5 block text-micro text-muted">{hint}</span>
                          <div className="mt-ds-3 grid gap-ds-2 sm:grid-cols-3">
                            {NOTIFICATION_CHANNEL_META.map(({ key: channelKey, label: channelLabel }) => (
                              <label
                                key={channelKey}
                                className="flex items-center gap-ds-2 rounded-lg border border-border/40 bg-card px-ds-2 py-ds-2 text-caption text-foreground"
                              >
                                <input
                                  type="checkbox"
                                  className="h-4 w-4 rounded border-border text-primary focus:ring-primary/20"
                                  checked={prefs[channelKey]}
                                  onChange={(e) => {
                                    onFieldEdit();
                                    setAlertRecipients({
                                      [key]: {
                                        ...prefs,
                                        [channelKey]: e.target.checked,
                                      },
                                    });
                                  }}
                                />
                                <span>{channelLabel}</span>
                              </label>
                            ))}
                          </div>
                          <div className="mt-ds-3 rounded-lg border border-border/40 bg-card px-ds-3 py-ds-3">
                            <label className="flex items-center gap-ds-2 text-caption text-foreground">
                              <input
                                type="checkbox"
                                className="h-4 w-4 rounded border-border text-primary focus:ring-primary/20"
                                checked={prefs.selectAll}
                                onChange={(e) => {
                                  onFieldEdit();
                                  setAlertRecipients({
                                    [key]: {
                                      ...prefs,
                                      selectAll: e.target.checked,
                                      userIds: e.target.checked ? [] : prefs.userIds,
                                    },
                                  });
                                }}
                              />
                              <span>Select all {label.toLowerCase()}</span>
                            </label>
                            <div className="mt-ds-3">
                              <label className="block text-micro text-muted">Selected users</label>
                              <ThemedMultiSelect
                                value={prefs.userIds}
                                onChange={(nextUserIds) => {
                                  onFieldEdit();
                                  setAlertRecipients({
                                    [key]: {
                                      ...prefs,
                                      userIds: nextUserIds,
                                    },
                                  });
                                }}
                                options={roleUsers.map((user) => ({
                                  value: user.id,
                                  label: userDisplayName(user),
                                  description: user.email,
                                }))}
                                placeholder={`Choose ${label.toLowerCase()}...`}
                                searchPlaceholder={`Search ${label.toLowerCase()}...`}
                                disabled={prefs.selectAll || roleUsers.length === 0}
                                ariaLabel={`${label} users`}
                                className="mt-ds-2"
                              />
                              <p className="mt-ds-2 text-micro text-muted">
                                {prefs.selectAll
                                  ? `All ${label.toLowerCase()} in this workspace will be notified.`
                                  : roleUsers.length > 0
                                    ? `${prefs.userIds.length} selected.`
                                    : `No ${label.toLowerCase()} found in this workspace.`}
                              </p>
                            </div>
                          </div>
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div>
                <p className={wizardLabelClass}>Additionally notify</p>
                <div className="mt-ds-2 grid gap-ds-3">
                  <div className="rounded-control border border-card-border bg-surface-primary p-ds-3">
                    <label className="block text-caption font-medium text-foreground">Email</label>
                    <p className="mt-0.5 text-micro text-muted">Comma-separated addresses beyond the audience cards above.</p>
                    <div className="mt-ds-3">
                      <input
                        className={panelInputClass()}
                        value={alertRecipients.emailRecipients}
                        onChange={(e) => {
                          onFieldEdit();
                          setAlertRecipients({ emailRecipients: e.target.value });
                        }}
                        placeholder="ops@example.com, admin@example.com"
                      />
                    </div>
                  </div>

                  <div className="rounded-control border border-card-border bg-surface-primary p-ds-3">
                    <label className="block text-caption font-medium text-foreground">Message</label>
                    <p className="mt-0.5 text-micro text-muted">Comma-separated phone numbers for SMS or messaging alerts.</p>
                    <div className="mt-ds-3">
                      <input
                        className={panelInputClass()}
                        value={alertRecipients.messageRecipients}
                        onChange={(e) => {
                          onFieldEdit();
                          setAlertRecipients({ messageRecipients: e.target.value });
                        }}
                        placeholder="+919876543210, +911234567890"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

export function LifecycleTab({ onFieldEdit }: { onFieldEdit: () => void }) {
  const lifecycle = useDataloggerWizardStore((s) => s.lifecycle);
  const setLifecycle = useDataloggerWizardStore((s) => s.setLifecycle);
  const user = useAuthStore((s) => s.user);
  const warrantyPreview = useWarrantyPreview(lifecycle.warrantyEnd);
  const warrantyEndLabel =
    warrantyPreview.warrantyEnd || (warrantyPreview.isLoading ? "Loading workspace policy..." : "Set by workspace policy");
  const isSuperAdmin = user?.role === "super_admin";
  const editableWarrantyEnd = lifecycle.warrantyEnd || warrantyPreview.warrantyEnd || "";

  function updateLifecycle(key: keyof typeof lifecycle, value: string) {
    onFieldEdit();
    setLifecycle({ [key]: value });
  }

  return (
    <div className="grid gap-ds-4 lg:grid-cols-[minmax(0,1fr)_18rem]">
      <Card className={cn(wizardSectionShellClass, "space-y-ds-4")}>
        <div>
          <p className={wizardKickerClass}>Lifecycle</p>
          <h3 className="mt-ds-1 text-title font-semibold text-foreground">Service dates</h3>
          <p className="mt-ds-1 text-caption text-muted">
            {isSuperAdmin
              ? "Warranty expiry defaults from workspace entitlement policy, and super admins can override it."
              : "Warranty expiry is assigned by workspace entitlement policy after provisioning."}
          </p>
        </div>

        <div className="grid gap-ds-3 sm:grid-cols-2 xl:grid-cols-3">
          <label className="space-y-ds-1">
            <span className={wizardLabelClass}>Installation date</span>
            <input
              type="date"
              className={panelInputClass()}
              value={lifecycle.installationDate}
              onChange={(e) => updateLifecycle("installationDate", e.target.value)}
            />
          </label>
          <label className="space-y-ds-1">
            <span className={wizardLabelClass}>Next calibration</span>
            <input
              type="date"
              className={panelInputClass()}
              value={lifecycle.nextCalibration}
              onChange={(e) => updateLifecycle("nextCalibration", e.target.value)}
            />
          </label>
          <label className="space-y-ds-1">
            <span className={wizardLabelClass}>Warranty end</span>
            {isSuperAdmin ? (
              <input
                type="date"
                className={panelInputClass()}
                value={editableWarrantyEnd}
                onChange={(e) => updateLifecycle("warrantyEnd", e.target.value)}
              />
            ) : (
              <input
                type="text"
                className={cn(panelInputClass(), "cursor-not-allowed bg-muted-bg/35 text-muted")}
                value={warrantyEndLabel}
                readOnly
                disabled
              />
            )}
          </label>
        </div>
      </Card>

      <Card className={wizardSectionShellClass}>
        <p className={wizardKickerClass}>Metadata keys</p>
        <dl className="mt-ds-4 space-y-ds-3 text-caption">
          <div>
            <dt className="text-muted">installation_date</dt>
            <dd className="mt-0.5  text-foreground">{lifecycle.installationDate || "—"}</dd>
          </div>
          <div>
            <dt className="text-muted">warranty_end</dt>
            <dd className="mt-0.5  text-foreground">{warrantyEndLabel}</dd>
          </div>
          <div>
            <dt className="text-muted">next_calibration</dt>
            <dd className="mt-0.5  text-foreground">{lifecycle.nextCalibration || "—"}</dd>
          </div>
        </dl>
      </Card>
    </div>
  );
}

export function DataloggerCreateWizard({ onCancel }: { onCancel?: () => void }) {
  const step = useDataloggerWizardStore((s) => s.step);
  const setStep = useDataloggerWizardStore((s) => s.setStep);
  const reset = useDataloggerWizardStore((s) => s.reset);
  const getSnapshot = useDataloggerWizardStore((s) => s.getSnapshot);

  const [stepErrors, setStepErrors] = useState<string[]>([]);
  const [invalidFieldKey, setInvalidFieldKey] = useState<string | null>(null);
  const lastStep = WIZARD_STEP_LABELS.length - 1;
  const snapshot = getSnapshot();

  const tabSummary = useMemo(
    () => [
      snapshot.basic.location || "Identity and location",
      `${snapshot.channels.length} channel${snapshot.channels.length === 1 ? "" : "s"}`,
      snapshot.visualizations.windRose.enabled ? "Wind rose enabled" : "Optional",
      samplingPeriodLabel(snapshot.hardware.samplingRatePeriod),
      `${snapshot.alerts.filter((alert) => alert.enabled).length} condition${snapshot.alerts.filter((alert) => alert.enabled).length === 1 ? "" : "s"}`,
      snapshot.hardware.transmissionMode,
      snapshot.lifecycle.installationDate || snapshot.lifecycle.nextCalibration || "Dates optional",
      snapshot.qrPublic.enabled ? `${snapshot.qrPublic.displayFields.length} public fields` : "Disabled",
      "Provision",
    ],
    [snapshot],
  );
  const stepTabItems = useMemo(
    () =>
      WIZARD_STEP_LABELS.map((label, i) => ({
        id: String(i),
        label,
        summary: tabSummary[i] ?? "",
        icon: TAB_ICONS[i] ?? CheckCircle2,
        complete: i < step && validateCreateTab(i, snapshot).length === 0,
      })),
    [snapshot, step, tabSummary],
  );

  function trySetStep(next: number) {
    const direction = next > step ? 1 : -1;
    if (direction > 0) {
      const errs = validateCreateTab(step, getSnapshot());
      if (errs.length > 0) {
        setStepErrors(errs);
        setInvalidFieldKey(invalidFieldKeyForError(errs[0]!) ?? null);
        return;
      }
    }
    setStepErrors([]);
    setInvalidFieldKey(null);
    setStep(Math.max(0, Math.min(lastStep, next)));
  }

  function clearValidationState() {
    if (stepErrors.length > 0 || invalidFieldKey) {
      setStepErrors([]);
      setInvalidFieldKey(null);
    }
  }

  useEffect(() => {
    if (!invalidFieldKey) {
      return;
    }
    const id = window.setTimeout(() => {
      const el = document.querySelector<HTMLElement>(`[${INVALID_FIELD_ATTR}="${invalidFieldKey}"]`);
      if (!el) {
        return;
      }
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      if (typeof el.focus === "function") {
        el.focus({ preventScroll: true });
      }
    }, 50);
    return () => window.clearTimeout(id);
  }, [invalidFieldKey]);

  return (
    <div className="space-y-ds-6 pb-28">
      <StepTabs
        ariaLabel="Create data logger sections"
        items={stepTabItems}
        activeId={String(step)}
        onSelect={(id) => trySetStep(Number(id))}
        hideProgressHeader
      />

      {stepErrors.length > 0 ? (
        <div className="border-b border-error/20 bg-error/8 px-ds-4 py-ds-4 sm:px-ds-6" role="alert">
          <p className="text-caption font-semibold text-error">Complete the required fields before continuing</p>
          <ul className="mt-ds-2 space-y-ds-1">
            {stepErrors.slice(0, 4).map((error) => (
              <li key={error} className="text-caption text-error/90">
                {error}
              </li>
            ))}
            {stepErrors.length > 4 ? (
              <li className="text-caption text-error/75">+ {stepErrors.length - 4} more</li>
            ) : null}
          </ul>
        </div>
      ) : null}

      <div className="bg-background">
          {step === 0 ? (
            <StepBasic
              includeConnectivity={false}
              invalidFieldKey={invalidFieldKey}
              validationErrors={stepErrors}
              onFieldEdit={clearValidationState}
            />
          ) : null}
          {step === 1 ? (
            <StepChannels
              invalidFieldKey={invalidFieldKey}
              validationErrors={stepErrors}
              onFieldEdit={clearValidationState}
            />
          ) : null}
          {step === 3 ? (
            <DataSamplingTab invalidFieldKey={invalidFieldKey} onFieldEdit={clearValidationState} />
          ) : null}
          {step === 2 ? (
            <VisualizationsTab invalidFieldKey={invalidFieldKey} onFieldEdit={clearValidationState} />
          ) : null}
          {step === 4 ? (
            <AlertsTab
              invalidFieldKey={invalidFieldKey}
              validationErrors={stepErrors}
              onFieldEdit={clearValidationState}
            />
          ) : null}
          {step === 5 ? (
            <StepBasicConnectivity
              invalidFieldKey={invalidFieldKey}
              validationErrors={stepErrors}
              onFieldEdit={clearValidationState}
            />
          ) : null}
          {step === 6 ? <LifecycleTab onFieldEdit={clearValidationState} /> : null}
          {step === 7 ? <QrPublicTab validationError={stepErrors[0] ?? null} /> : null}
          {step === 8 ? <StepReview /> : null}
      </div>

      {step < lastStep ? (
        <div className="hidden">
          <span className="text-caption text-muted">
            {step + 1} / {WIZARD_STEP_LABELS.length} · {WIZARD_STEP_LABELS[step]}
          </span>
          <div className="flex items-center gap-ds-2">
            <Button type="button" variant="secondary" size="md" disabled={step === 0} onClick={() => trySetStep(step - 1)}>
              <ChevronLeft className="h-4 w-4" aria-hidden />
              Back
            </Button>
            <Button type="button" size="md" onClick={() => trySetStep(step + 1)}>
              Continue
              <ChevronRight className="h-4 w-4" aria-hidden />
            </Button>
          </div>
        </div>
      ) : null}

      <div className="pointer-events-none fixed inset-x-0 bottom-5 z-[1100] flex justify-center px-ds-3 md:left-[var(--sidebar-width-collapsed)]">
        <div className="pointer-events-auto inline-flex max-w-full flex-wrap items-center justify-center gap-ds-2 rounded-control border border-primary/20 bg-[var(--accent-primary-soft)] px-ds-3 py-ds-2 shadow-overlay backdrop-blur-md">
          <Button type="button" variant="secondary" size="sm" onClick={onCancel} disabled={!onCancel}>Cancel</Button>
          <Button type="button" variant="secondary" size="sm" disabled={step === 0} onClick={() => trySetStep(step - 1)}><ChevronLeft className="h-4 w-4" aria-hidden />Prev</Button>
          <Button type="button" variant="secondary" size="sm" disabled={step === lastStep} onClick={() => trySetStep(step + 1)}>Next<ChevronRight className="h-4 w-4" aria-hidden /></Button>
          <Button type="button" variant="secondary" size="sm" onClick={() => { reset(); setStep(0); setStepErrors([]); setInvalidFieldKey(null); }}><Undo2 className="h-4 w-4" aria-hidden />Reset</Button>
        </div>
      </div>
    </div>
  );
}
